/**
 * Panel365 — 365 dashboard-channel panel (T3-P1b).
 *
 * Part A: Instruction input — Duncan types a free-text instruction and
 *   submits it via sendInstruction() → POST /api/365/instruct.
 *
 * Part B: Response channel — chronological list of Thread365Messages
 *   returned by GET /api/365/thread (polled at the dashboard's standard
 *   POLL_MS cadence via useDashboard).
 */

import { useState, useRef } from "react";
import type { Thread365Message } from "../types";
import "./Panel365.css";

/**
 * Format an ISO-8601 timestamp into a compact local display string.
 * Falls back to the raw string if parsing fails.
 */
function fmtTimestamp(iso: string): string {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  } catch {
    return iso;
  }
}

type Props = {
  thread365: Thread365Message[];
  sendInstruction: (text: string) => Promise<{ section_id: string }>;
};

export default function Panel365({ thread365, sendInstruction }: Props) {
  const [inputText, setInputText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [sentRef, setSentRef] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const text = inputText.trim();
    if (!text || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    setSentRef(null);
    try {
      const result = await sendInstruction(text);
      setInputText("");
      setSentRef(result.section_id);
      textareaRef.current?.focus();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section
      className={`panel panel-365 ${thread365.length === 0 ? "is-empty" : "is-populated"}`}
      aria-labelledby="panel-365-title"
    >
      <header className="panel-head">
        <h2 className="panel-title" id="panel-365-title">
          365 Channel
        </h2>
        <span className="panel-meta">
          <span className="panel-count">{thread365.length}</span>
          <span className="panel-sub">
            responses from boardroom-365 · polled every 15s
          </span>
        </span>
      </header>

      {/* Part A — instruction input */}
      <form className="instruct-form" onSubmit={handleSubmit} aria-label="Send instruction">
        <textarea
          ref={textareaRef}
          className="instruct-textarea"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          placeholder="Type an instruction for boardroom-365…"
          disabled={submitting}
          rows={2}
          aria-label="Instruction text"
          onKeyDown={(e) => {
            // Ctrl+Enter or Cmd+Enter to submit
            if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
              e.preventDefault();
              void handleSubmit(e as unknown as React.FormEvent);
            }
          }}
        />
        <button
          type="submit"
          className="instruct-submit"
          disabled={submitting || !inputText.trim()}
        >
          {submitting ? "Sending…" : "Send"}
        </button>
      </form>

      {submitError && (
        <div className="instruct-error" role="alert">
          Send failed: {submitError}
        </div>
      )}
      {sentRef && !submitError && (
        <div className="instruct-sent" aria-live="polite">
          Sent · ref: {sentRef}
        </div>
      )}

      {/* Part B — response channel */}
      <div className="panel-body">
        {thread365.length === 0 && (
          <div className="thread-empty">No responses yet from boardroom-365.</div>
        )}
        {thread365.map((msg) => (
          <div key={msg.ref} className="thread-row">
            <span className="thread-meta">
              <span className="thread-label">365</span>
              <span
                className="thread-ts"
                title={msg.timestamp}
              >
                {fmtTimestamp(msg.timestamp)}
              </span>
            </span>
            <span className="thread-text">{msg.text}</span>
          </div>
        ))}
      </div>
    </section>
  );
}
